export type ComparisonRow = {
  feature: string;
  cently: string;
  competitor: string;
};

export type ComparisonFaq = {
  q: string;
  a: string;
};

export type ComparisonEntry = {
  slug: string;
  competitor: string;
  h1: string;
  seoTitle: string;
  metaDescription: string;
  keywords: string[];
  intro: string[];
  rows: ComparisonRow[];
  whyCently: string[];
  faq: ComparisonFaq[];
  relatedBlogSlug: string;
};

export const comparisons: ComparisonEntry[] = [
  {
    slug: 'mint-alternative',
    competitor: 'Mint',
    h1: 'Cently vs Mint: a practical Mint alternative',
    seoTitle: 'Mint alternative: Cently vs Mint for modern money tracking',
    metaDescription:
      'Looking for a Mint alternative? Compare Cently vs Mint across AI money chat, receipt tracking, and cross-platform access on web, iOS, and Android.',
    keywords: [
      'mint alternative',
      'mint replacement',
      'apps like mint',
      'budgeting app alternative to mint',
      'money tracking app',
    ],
    intro: [
      'If you used Mint and need a replacement, you probably want one place to track spending without rebuilding your workflow from scratch.',
      'Cently gives you linked accounts, budget tracking, receipt parsing, and AI chat so you can ask clear questions about your spending and get next steps.',
    ],
    rows: [
      {
        feature: 'Account aggregation',
        cently: 'Plaid linking plus CSV import for unsupported institutions',
        competitor: 'Mint service ended, so active account syncing is unavailable',
      },
      {
        feature: 'Ask questions about your money',
        cently: 'Built-in AI finance chat with follow-up context',
        competitor: 'No active product experience',
      },
      {
        feature: 'Receipt workflows',
        cently: 'Upload or capture receipts with parsed line items',
        competitor: 'No active product experience',
      },
      {
        feature: 'Platforms',
        cently: 'Web, iOS, and Android',
        competitor: 'No active product experience',
      },
    ],
    whyCently: [
      'You can ask natural questions like "Where did dining spike this month?" and get clear answers fast.',
      'You can tie receipts directly to transactions to keep records clean for reimbursements and taxes.',
      'You can use the same account on web, iOS, and Android instead of being locked to one device.',
    ],
    faq: [
      {
        q: 'Is Cently a good Mint replacement?',
        a: 'Cently is built for people who want automatic tracking plus guided insights. You get linked accounts, budgeting, receipts, and AI chat in one workflow.',
      },
      {
        q: 'Can I move to Cently if my old budgeting data was in Mint?',
        a: 'Yes. You can reconnect institutions through Plaid and use CSV import to bring in historical data where needed.',
      },
      {
        q: 'Does Cently work on mobile and desktop?',
        a: 'Yes. Cently works on web, iOS, and Android with one account.',
      },
    ],
    relatedBlogSlug: 'alerts-and-budgets',
  },
  {
    slug: 'rocket-money-alternative',
    competitor: 'Rocket Money',
    h1: 'Cently vs Rocket Money: which app fits daily tracking?',
    seoTitle:
      'Rocket Money alternative: compare Cently vs Rocket Money features',
    metaDescription:
      'Compare Cently vs Rocket Money for budgeting, transaction analysis, receipt workflows, and AI finance chat.',
    keywords: [
      'rocket money alternative',
      'apps like rocket money',
      'budget tracker alternatives',
      'personal finance app comparison',
    ],
    intro: [
      'Rocket Money and Cently both help you track spending, but the day-to-day experience is different.',
      'Cently is built around fast answers from your own transaction data, plus receipt-level detail when you need to audit or clean up records.',
    ],
    rows: [
      {
        feature: 'AI analysis',
        cently: 'Ask questions in chat and get actionable spending insights',
        competitor: 'Rule-based summaries and standard dashboard views',
      },
      {
        feature: 'Receipt detail',
        cently: 'Receipt capture and parsed line-item review',
        competitor: 'Receipts are not a core workflow',
      },
      {
        feature: 'Cross-platform access',
        cently: 'One account across web, iOS, and Android',
        competitor: 'Mobile-first workflow with web support varying by feature',
      },
      {
        feature: 'Data import flexibility',
        cently: 'Plaid plus CSV import to fill data gaps',
        competitor: 'Primarily linked-account based',
      },
    ],
    whyCently: [
      'You can move from dashboard browsing to direct Q&A when you need answers quickly.',
      'You can keep both linked transactions and receipt evidence in one place.',
      'You can combine Plaid links with CSV imports to reduce missing data.',
    ],
    faq: [
      {
        q: 'Why choose Cently over Rocket Money?',
        a: 'Choose Cently if you want AI chat for spending analysis and built-in receipt workflows, not just top-level summaries.',
      },
      {
        q: 'Does Cently include budgeting features?',
        a: 'Yes. You can set category budgets, track pacing, and receive budget alerts.',
      },
      {
        q: 'Can I try Cently before paying?',
        a: 'Yes. Cently offers a 7-day free trial.',
      },
    ],
    relatedBlogSlug: 'alerts-and-budgets',
  },
  {
    slug: 'copilot-money-alternative',
    competitor: 'Copilot Money',
    h1: 'Cently vs Copilot Money: compare flexibility and insight depth',
    seoTitle:
      'Copilot Money alternative: Cently vs Copilot for budgeting and tracking',
    metaDescription:
      'Looking for a Copilot Money alternative? Compare Cently and Copilot across AI chat, receipt tracking, and web + mobile availability.',
    keywords: [
      'copilot money alternative',
      'apps like copilot money',
      'copilot budgeting alternative',
      'finance app web and mobile',
    ],
    intro: [
      'Copilot Money is known for polished budgeting workflows and a strong Apple experience.',
      'Cently is a stronger fit when you want native Android access alongside web and iOS, plus AI chat that answers real spending questions from your own data.',
    ],
    rows: [
      {
        feature: 'Device coverage',
        cently: 'Web, iOS, and Android',
        competitor: 'iPhone, iPad, Mac, and web, with no native Android app',
      },
      {
        feature: 'AI guidance',
        cently: 'Conversation-based insights with follow-up context',
        competitor: 'Machine-learning categorization and dashboard analysis',
      },
      {
        feature: 'Receipts and transaction proof',
        cently: 'Receipts can be uploaded, parsed, matched, and edited',
        competitor: 'Receipts are not the primary product focus',
      },
      {
        feature: 'Import options',
        cently: 'Plaid + CSV support',
        competitor: 'Primarily connected account workflows',
      },
    ],
    whyCently: [
      'You can use one finance workflow across web and both major mobile platforms, including Android.',
      'You can ask direct questions instead of manually slicing charts every time.',
      'You can audit purchases at line-item level with receipt parsing.',
    ],
    faq: [
      {
        q: 'Does Copilot Money work on Android?',
        a: 'Copilot offers iPhone, iPad, Mac, and web apps but has no native Android app. Cently runs on web, iOS, and Android.',
      },
      {
        q: 'Can Cently help with receipts?',
        a: 'Yes. Cently includes receipt upload, parsing, matching, and editing.',
      },
      {
        q: 'Does Cently support linked bank accounts?',
        a: 'Yes. You can connect institutions through Plaid and supplement with CSV imports.',
      },
    ],
    relatedBlogSlug: 'import-progress-tells-the-story',
  },
  {
    slug: 'monarch-money-alternative',
    competitor: 'Monarch Money',
    h1: 'Cently vs Monarch Money: compare planning and execution',
    seoTitle:
      'Monarch Money alternative: Cently vs Monarch for spending insights',
    metaDescription:
      'Compare Cently and Monarch Money for budgeting, account syncing, receipt tracking, and AI-powered spending analysis.',
    keywords: [
      'monarch money alternative',
      'apps like monarch money',
      'budget planning app alternative',
      'personal finance tracking tools',
    ],
    intro: [
      'Monarch Money is strong for household planning, investment tracking, and broad account visibility.',
      'Cently is a simpler, spending-first option with receipt-level detail and AI chat, which fits people who do not need full household and investment planning.',
    ],
    rows: [
      {
        feature: 'AI assistant',
        cently: 'AI chat that answers spending questions and suggests next steps',
        competitor:
          'AI assistant that answers natural-language questions about your data',
      },
      {
        feature: 'Receipts',
        cently: 'Capture, parse, and match receipts to transactions',
        competitor: 'Receipts are not a primary capability',
      },
      {
        feature: 'Planning depth',
        cently: 'Focused on spending, budgets, and receipts',
        competitor: 'Household collaboration and investment tracking',
      },
      {
        feature: 'Platform support',
        cently: 'Web, iOS, and Android',
        competitor: 'Web, iOS, and Android',
      },
    ],
    whyCently: [
      'You get receipt capture and line-item detail tied to transactions, which is not a Monarch focus.',
      'You get a simpler, spending-first workflow if you do not need household and investment planning depth.',
      'You can still ask questions in chat and act on clear next steps.',
    ],
    faq: [
      {
        q: 'Should I choose Cently or Monarch Money?',
        a: 'Choose Cently for a simpler spending-first workflow with receipt detail and AI chat. Monarch is a better fit if you want deep household collaboration and investment planning.',
      },
      {
        q: 'Does Cently support category budgets?',
        a: 'Yes. You can track category budgets and receive alerts when spending drifts.',
      },
      {
        q: 'Can I access Cently from desktop and mobile?',
        a: 'Yes. Cently supports web, iOS, and Android.',
      },
    ],
    relatedBlogSlug: 'alerts-and-budgets',
  },
  {
    slug: 'ynab-alternative',
    competitor: 'YNAB',
    h1: 'Cently vs YNAB: simplicity vs manual-first budgeting',
    seoTitle: 'YNAB alternative: Cently vs YNAB for automated tracking',
    metaDescription:
      'Looking for a YNAB alternative? Compare Cently and YNAB across automation, AI insights, receipt workflows, and platform support.',
    keywords: [
      'ynab alternative',
      'you need a budget alternative',
      'automated budgeting app',
      'spending tracker with ai',
    ],
    intro: [
      'YNAB is built around a strict zero-based budgeting method that many people love, especially if they want full manual control.',
      'Cently is designed for people who want automation first, with AI insights and receipt-backed transaction detail without manual upkeep.',
    ],
    rows: [
      {
        feature: 'Budgeting style',
        cently: 'Automated tracking with category budgets and alerts',
        competitor:
          'Hands-on zero-based method where you assign every dollar (bank import supported)',
      },
      {
        feature: 'AI money chat',
        cently: 'Built-in chat for spending analysis and next steps',
        competitor: 'No equivalent conversation-first AI workflow',
      },
      {
        feature: 'Receipt workflows',
        cently: 'Capture, parse, edit, and match receipts',
        competitor: 'Receipts are not a primary product feature',
      },
      {
        feature: 'Setup effort',
        cently: 'Fast start with linked accounts and optional CSV import',
        competitor: 'Higher involvement to maintain category discipline',
      },
    ],
    whyCently: [
      'You can keep budgeting lightweight while still seeing clear category trends.',
      'You can ask questions in plain English when spending feels unclear.',
      'You can avoid spreadsheet-style maintenance and still stay informed.',
    ],
    faq: [
      {
        q: 'Is Cently better than YNAB for beginners?',
        a: 'Cently is often easier for beginners who want automation and quick answers without learning a strict budgeting framework first.',
      },
      {
        q: 'Can Cently still support disciplined budgeting?',
        a: 'Yes. Cently supports category budgets and alerting while keeping tracking mostly automatic.',
      },
      {
        q: 'Do I have to manually enter every purchase?',
        a: 'No. Linked accounts and CSV import handle most transaction input automatically.',
      },
    ],
    relatedBlogSlug: 'alerts-and-budgets',
  },
];

export const comparisonSlugs = comparisons.map((entry) => entry.slug);

export function getComparisonBySlug(slug: string): ComparisonEntry | undefined {
  return comparisons.find((entry) => entry.slug === slug);
}
